import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import {
  Bucket,
  Buckets,
  Event,
  MiButton,
  MiDatePicker,
  MiInputTextbox,
  MiList,
  MiLoader,
  MiModal,
  MiRadioGroup,
  MiStackedTypography,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  COLUMN_WIDTH,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  DATE_TIME_FORMATS,
  HTTP_STATUSES,
  PLATFORM_MODULES,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useBoolean, useEdit } from "@miplatform/hooks";
import { CalendarEventType } from "@miplatform/types";
import {
  getDate,
  getTimeDifferenceDurationText,
  isEmpty,
  toFormattedDate,
} from "@miplatform/utils";
import {
  mdiCalendarBlank,
  mdiFileDocumentOutline,
  mdiHandFrontLeft,
} from "@mdi/js";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { RescheduleOrderModal } from "../../Orders/OrdersList/ActionMenu/RescheduleOrderModal";
import { OrderCardOrderTypeField } from "../../Orders/OrdersList/OrderCardOrderTypeField/OrderCardOrderTypeField";
import { HoldsActionMenu } from "./HoldsActionMenu/HoldsActionMenu";
import { LogFollowUpModal } from "./LogFollowUpModal/LogFollowUpModal";
import { useHoldsViewModel } from "./useHoldsViewModel";

const PLACEHOLDER_TEXT = "";

const OPTION_RELEASE_NOW = "release_now";

const RELEASE_OPTIONS = [
  { label: "Release now", value: OPTION_RELEASE_NOW },
  { label: "Release on a future date", value: "release_future" },
];

const INITIAL_RELEASE_EDIT = {
  option: OPTION_RELEASE_NOW,
  reason: PLACEHOLDER_TEXT,
};

const HOLD_COLUMN_WIDTH = {
  holdPeriod: 110,
  followUpDate: 160,
  holdReason: 200,
  orderType: 160,
};

export const Holds = ({
  calendar: activeCalendar,
  hideActionMenu,
  refresh,
  isLoading,
  holdsData,
  getHoldsData,
  updateCalendarCounts,
  buckets = [],
  EventCard = () => null,
  activeTab,
  count,
  calendarSet,
  viewConfig,
  onCloseoutDrawerOpen,
  orderReasons,
  module,
  skipEventWrapper = false,
}) => {
  const today = toFormattedDate(getDate());

  const [selectedAttempt, setSelectedAttempt] = useState({});

  const [
    isFollowUpModalOpen,
    handleFollowUpModalOpen,
    handleFollowUpModalClose,
  ] = useBoolean();
  const [
    isRescheduleModalOpen,
    handleRescheduleModalOpen,
    handleRescheduleModalClose,
  ] = useBoolean();
  const [isReleaseModalOpen, handleReleaseModalOpen, handleReleaseModalClose] =
    useBoolean();
  const calendar = useMemo(
    () =>
      activeCalendar ||
      calendarSet.calendarConfigs.find(({ id }) => id === viewConfig.id),
    [calendarSet, viewConfig, activeCalendar]
  );

  const releaseEdit = useEdit({ ...INITIAL_RELEASE_EDIT, date: today });

  useEffect(() => {
    updateCalendarCounts();
  }, [activeTab]);

  useEffect(() => {
    if (releaseEdit.getValue("option") === OPTION_RELEASE_NOW) {
      releaseEdit.update({ date: today });
    }
  }, [releaseEdit.getValue("option")]);

  useEffect(() => {
    getHoldsData();
  }, [activeTab, refresh, count]);

  const {
    isLoading: isFetching,
    releaseOrder,
    releaseWorkOrder,
    rescheduleOrder,
  } = useHoldsViewModel();

  const { classes, theme } = useStyles();

  const groupedTitle = (title) => `Follow-Up on: ${title}`;
  const groupedCriteria = (item) =>
    toFormattedDate(
      item.assignment.order?.followUpDate ||
        item.assignment.workOrder?.followUpDate,
      "MMMM DD, YYYY"
    );

  const sections = [
    {
      fieldset: [
        {
          renderCell: (attempt) => {
            const { assignmentTemplateShortName } = attempt.assignment;
            const {
              holdReason,
              followUpDate,
              holdStartDate,
              orderTemplateAbbreviation,
              orderTemplateColor,
              orderTemplateName,
              abbreviation,
              workOrderTemplateColor,
              workOrderTemplateName,
            } = attempt.assignment.order || attempt.assignment.workOrder;

            return (
              <Box className={classes.cardRow}>
                <Box className={classes.titleSection}>
                  <Box className={classes.titleChildSection}>
                    <Typography
                      variant={TYPOGRAPHY_VARIANTS.LIST_TITLE}
                      color="main.primary"
                      fontWeight={600}
                      fontSize={14}
                      noWrap
                    >
                      {attempt.home?.address?.addressLine1 ||
                        attempt.address?.addressLine1 ||
                        PLACEHOLDER_TEXT}
                    </Typography>
                    <Typography
                      variant={TYPOGRAPHY_VARIANTS.BODY2}
                      color="textSecondary"
                      noWrap
                    >
                      {attempt.home?.builderName || attempt.builder}
                    </Typography>
                  </Box>
                  <Box className={classes.titleChildSection}>
                    <Typography
                      variant={TYPOGRAPHY_VARIANTS.BODY2}
                      color="textSecondary"
                      noWrap
                    >
                      {attempt.home?.address?.city ||
                        attempt.address?.city ||
                        PLACEHOLDER_TEXT}
                      ,{" "}
                      {attempt.home?.address?.regionCode ||
                        attempt.address?.regionCode ||
                        PLACEHOLDER_TEXT}
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                    <Typography
                      variant={TYPOGRAPHY_VARIANTS.BODY2}
                      color="textSecondary"
                      noWrap
                    >
                      {attempt.home?.communityName ||
                        attempt.community ||
                        "(No Community Info)"}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.actions}>
                  <MiButton
                    title="Log Follow-Up"
                    startIcon={{
                      path: mdiFileDocumentOutline,
                      iconSize: 0.8,
                      iconColor: theme.palette.primary.main,
                    }}
                    variant={BUTTON_VARIANTS.OUTLINED}
                    className={classes.releaseBtn}
                    size="small"
                    onClick={() => {
                      setSelectedAttempt(attempt);
                      handleFollowUpModalOpen();
                    }}
                  />
                  {module === PLATFORM_MODULES.MI_DISPATCH && (
                    <MiButton
                      title="Release Order"
                      variant={BUTTON_VARIANTS.CONTAINED}
                      className={classes.releaseBtn}
                      size="small"
                      onClick={() => {
                        handleReleaseModalOpen();
                        setSelectedAttempt(attempt);
                      }}
                    />
                  )}
                  {module === PLATFORM_MODULES.MI_SCHEDULING &&
                    calendarSet?.config?.eventType ===
                      CalendarEventType.WorkOrder && (
                      <MiButton
                        title="Release Work Order"
                        variant={BUTTON_VARIANTS.CONTAINED}
                        className={classes.releaseBtn}
                        size="small"
                        onClick={() => {
                          handleReleaseModalOpen();
                          setSelectedAttempt(attempt);
                        }}
                      />
                    )}
                  {module === PLATFORM_MODULES.MI_SCHEDULING &&
                    calendarSet?.config?.eventType !==
                      CalendarEventType.WorkOrder && (
                      <MiButton
                        title="Schedule"
                        className={classes.releaseBtn}
                        sx={{
                          borderColor: theme.palette.blue.dark,
                          color: theme.palette.blue.dark,
                        }}
                        startIcon={{
                          path: mdiCalendarBlank,
                          iconSize: 0.8,
                          iconColor: theme.palette.blue.dark,
                        }}
                        onClick={() => {
                          handleRescheduleModalOpen();
                          setSelectedAttempt(attempt);
                        }}
                        variant={BUTTON_VARIANTS.OUTLINED}
                        data-testid="schedule-btn"
                      />
                    )}
                  {!hideActionMenu && (
                    <HoldsActionMenu
                      onDetailsClick={() => onCloseoutDrawerOpen(attempt)}
                    />
                  )}
                </Box>
                <Box className={classes.holdOrderListItems}>
                  <Box
                    sx={{ width: HOLD_COLUMN_WIDTH.holdPeriod, flexShrink: 0 }}
                  >
                    <MiStackedTypography
                      title="Hold Period"
                      description={getTimeDifferenceDurationText({
                        start: moment(holdStartDate),
                        end: moment.now(),
                      })}
                      descriptionProps={{ fontWeight: theme.fontWeight.bold }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: HOLD_COLUMN_WIDTH.followUpDate,
                      flexShrink: 0,
                    }}
                  >
                    <MiStackedTypography
                      title="Follow-Up Date"
                      description={toFormattedDate(
                        followUpDate,
                        DATE_TIME_FORMATS.FORMAT_MM_DD_HH_MM_A
                      )}
                      descriptionProps={{ fontWeight: theme.fontWeight.bold }}
                    />
                  </Box>
                  <Box
                    sx={{ width: HOLD_COLUMN_WIDTH.holdReason, flexShrink: 0 }}
                  >
                    <MiStackedTypography
                      title="Hold Reason"
                      description={holdReason || PLACEHOLDER_TEXT}
                      descriptionProps={{ fontWeight: theme.fontWeight.bold }}
                    />
                  </Box>
                  <Box
                    sx={{ width: HOLD_COLUMN_WIDTH.orderType, flexShrink: 0 }}
                  >
                    <OrderCardOrderTypeField
                      title={workOrderTemplateName ? "Work Order" : "Order"}
                      orderTemplateAbbreviation={
                        orderTemplateAbbreviation || abbreviation
                      }
                      orderTemplateColor={
                        orderTemplateColor || workOrderTemplateColor
                      }
                      orderTemplateName={
                        orderTemplateName || workOrderTemplateName
                      }
                      orderTemplateShortName={assignmentTemplateShortName}
                    />
                  </Box>
                </Box>
              </Box>
            );
          },
        },
      ],
    },
  ];

  const handleRefresh = () => {
    getHoldsData();
    updateCalendarCounts();
  };

  const handleRelease = async () => {
    handleReleaseModalClose();

    const { status } = await (calendarSet?.config?.eventType ===
      CalendarEventType.WorkOrder
      ? releaseWorkOrder
      : releaseOrder)({
      attempt: selectedAttempt,
      reason: releaseEdit.getValue("reason"),
      ...(releaseEdit.getValue("option") === OPTION_RELEASE_NOW
        ? {}
        : { releaseDate: releaseEdit.getValue("date") }),
    });

    if (status === HTTP_STATUSES.OK) {
      handleRefresh();
    }
  };

  const handleOrderReschedule = async (data) => {
    const response = await rescheduleOrder(selectedAttempt.orderId, {
      ...data,
      modifiedOn: selectedAttempt.orderModifiedOn,
    });

    handleRescheduleModalClose(false);
    if (response) {
      handleRefresh();
    }
  };

  const handleReleaseOptionChange = ({ target: { value } }) => {
    releaseEdit.update({ option: value });
  };

  const onReleaseModelClose = () => {
    handleReleaseModalClose();
    releaseEdit.reset();
  };

  const onReleaseDateChange = (value) => {
    releaseEdit.update({ date: toFormattedDate(value) });
  };

  const onReasonChange = ({ target: { value } }) => {
    releaseEdit.update({ reason: value });
  };

  return (
    <Box className={classes.root}>
      <MiLoader isLoading={isLoading || isFetching} type="linear" />
      {!isLoading && (
        <>
          <MiList
            showGroupCount
            hideListPagination
            groupedMode
            viewType="card"
            cardProps={{ sections }}
            groupedCriteria={groupedCriteria}
            groupedTitle={groupedTitle}
            data={holdsData}
          />
          <Grid container item direction="column" flex={0}>
            <Buckets>
              {buckets.map((bucket) => (
                <Bucket key={bucket.id} bucket={bucket}>
                  {bucket.data.map((event) =>
                    skipEventWrapper ? (
                      <Box
                        key={event.id}
                        sx={{ width: COLUMN_WIDTH, flexShrink: 0 }}
                      >
                        <EventCard event={event} isInBucket />
                      </Box>
                    ) : (
                      <Event key={event.id} event={event} isInBucket>
                        <EventCard event={event} isInBucket />
                      </Event>
                    )
                  )}
                </Bucket>
              ))}
            </Buckets>
          </Grid>
        </>
      )}
      <LogFollowUpModal
        isOpen={isFollowUpModalOpen}
        attempt={selectedAttempt}
        calendar={calendar}
        calendarConfig={calendarSet?.config}
        onClose={handleFollowUpModalClose}
        onRefresh={handleRefresh}
      />
      <RescheduleOrderModal
        open={isRescheduleModalOpen}
        onClose={handleRescheduleModalClose}
        onReschedule={handleOrderReschedule}
        orderReasons={orderReasons}
        calendarConfig={calendarSet?.config}
        isLoading={isLoading}
      />
      {calendarSet?.config?.eventType === CalendarEventType.WorkOrder ? (
        <MiModal
          open={isReleaseModalOpen}
          closeModal={onReleaseModelClose}
          width={448}
          actions={[
            {
              name: "Cancel",
              onClick: onReleaseModelClose,
              variant: BUTTON_VARIANTS.TEXT,
              color: BUTTON_COLORS.BLACK,
            },
            {
              variant: BUTTON_VARIANTS.CONTAINED,
              color: BUTTON_COLORS.PRIMARY,
              name: "Release Work Order",
              onClick: handleRelease,
              disabled: isEmpty(releaseEdit.getValue("reason")),
            },
          ]}
          title="Release Work Order"
          titleIcon={mdiHandFrontLeft}
          leftIconColor={theme.palette.primary.main}
          description="Release will allow for the work order to be placed back onto the calendar."
        >
          <MiInputTextbox
            label="Reason"
            value={releaseEdit.getValue("reason")}
            handleChange={onReasonChange}
            required
          />
        </MiModal>
      ) : (
        <MiModal
          open={isReleaseModalOpen}
          closeModal={onReleaseModelClose}
          actions={[
            {
              name: "Cancel",
              onClick: onReleaseModelClose,
              variant: BUTTON_VARIANTS.TEXT,
              color: BUTTON_COLORS.BLACK,
            },
            {
              variant: BUTTON_VARIANTS.CONTAINED,
              color: BUTTON_COLORS.PRIMARY,
              name: "Release Order",
              onClick: handleRelease,
              disabled: isEmpty(releaseEdit.getValue("reason")),
            },
          ]}
          title="Release Order"
          titleIcon={mdiHandFrontLeft}
          leftIconColor={theme.palette.primary.main}
          description="Release will allow for the order to be placed back onto the calendar and determine which tray the order is placed into."
        >
          <Box className={classes.fields}>
            <MiRadioGroup
              data={RELEASE_OPTIONS}
              handleChange={handleReleaseOptionChange}
              value={releaseEdit.getValue("option")}
            />
            <MiDatePicker
              isFullWidth
              label="Date"
              value={releaseEdit.getValue("date")}
              onChange={onReleaseDateChange}
              disabled={releaseEdit.getValue("option") === OPTION_RELEASE_NOW}
              variant={DATEPICKER_VARIANTS.DESKTOP}
              actions={[DATEPICKER_ACTIONS.CANCEL]}
              format={DATE_TIME_FORMATS.FORMAT_MMMM_D_YYYY}
            />
            <MiInputTextbox
              label="Reason"
              value={releaseEdit.getValue("reason")}
              handleChange={onReasonChange}
              required
            />
            <MiInputTextbox
              disabled
              label="Tray"
              value={calendar?.attachedTray?.name}
            />
          </Box>
        </MiModal>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    height: "100%",
    overflowY: "auto",
    containerType: "inline-size",
  },
  cardRow: {
    display: "grid",
    alignItems: "start",
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1),
    width: "100%",
    gridTemplateColumns: "1fr auto",
    gridTemplateAreas: `
      "title actions"
      "columns columns"
    `,
    "@container (min-width: 1230px)": {
      gridTemplateColumns: "auto 1fr auto",
      gridTemplateAreas: `"title columns actions"`,
    },
  },
  holdOrderListItems: {
    gridArea: "columns",
    display: "flex",
    flexWrap: "nowrap",
    justifyContent: "flex-start",
    justifySelf: "start",
    gap: theme.spacing(4),
    marginLeft: theme.spacing(3),
    "& .MuiTypography-subtitle2": {
      fontSize: "14px !important",
    },
  },
  titleSection: {
    gridArea: "title",
    display: "flex",
    flexDirection: "column",
    minWidth: 300,
    maxWidth: 250,
    justifySelf: "start",
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  titleChildSection: {
    display: "flex",
    gap: theme.spacing(2),
    minWidth: 0,
  },
  fields: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
  actions: {
    gridArea: "actions",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    justifySelf: "end",
    paddingTop: theme.spacing(2),
  },
  releaseBtn: {
    padding: theme.spacing(0.5, 2),
    height: 26,
  },
}));
